<template>
  <div
    class="single-select-toggle-row"
    :class="[
      {
        [`size-${size}`]: true,
        [`variant-${variant}`]: true,
        'full-width': fullWidth,
        'no-wrap': !wrap,
        disabled,
      },
    ]"
    :style="gap ? { gap } : undefined"
    role="radiogroup"
    aria-orientation="horizontal"
    :aria-label="ariaLabel || undefined"
    :aria-disabled="disabled ? 'true' : undefined"
  >
    <button
      v-for="(opt, idx) in normalizedOptions"
      :key="opt.id"
      :ref="(element) => registerBtn(element, idx)"
      class="single-toggle"
      :class="{
        active: opt.id === currentValue,
        disabled: isOptionDisabled(opt),
      }"
      role="radio"
      :aria-checked="String(opt.id === currentValue)"
      :aria-disabled="String(disabled || isOptionDisabled(opt))"
      :tabindex="getTabIndex(opt, idx)"
      :title="opt.label"
      type="button"
      @click="onSelect(opt)"
      @keydown="onKeydown($event, idx)"
      @focus="onFocus(opt)"
      @blur="onBlur(opt)"
      :disabled="disabled || isOptionDisabled(opt)"
    >
      <span v-if="opt.icon" class="icon-wrapper">
        <component :is="opt.icon" v-if="isComponent(opt.icon)" />
        <svg v-else-if="isSpriteRef(opt.icon)" class="icon compactImg">
          <use :href="opt.icon" />
        </svg>
        <span v-else class="icon-text">{{ opt.icon }}</span>
      </span>
      <span class="label" v-if="opt.label">{{ opt.label }}</span>
      <span class="abbr" v-else-if="opt.abbr">{{ opt.abbr }}</span>
    </button>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted } from "vue";

export default {
  name: "SingleSelectToggleRow",
  props: {
    options: {
      type: Array,
      required: true,
      // Each option: { id: string|number, label?: string, abbr?: string, icon?: string|component, disabled?: boolean }
    },
    modelValue: {
      type: [String, Number, null],
      default: null,
    },
    size: {
      type: String,
      default: "md", // sm | md | lg
      validator: (v) => ["sm", "md", "lg"].includes(v),
    },
    variant: {
      type: String,
      default: "solid", // solid | outline | ghost | segmented
      validator: (v) =>
        ["solid", "outline", "ghost", "segmented"].includes(v),
    },
    wrap: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    optionDisabledKey: {
      type: String,
      default: "disabled",
    },
    ariaLabel: {
      type: String,
      default: "",
    },
    pressOnFocus: {
      type: Boolean,
      default: false,
    },
    automaticFocus: {
      type: Boolean,
      default: false,
    },
    autoSelectFirst: {
      type: Boolean,
      default: false,
    },
    gap: {
      type: String,
      default: "",
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "change", "focus", "blur"],
  setup(props, { emit }) {
    const currentValue = ref(props.modelValue);

    const normalizedOptions = computed(() =>
      props.options.map((o) => ({ ...o }))
    );

    const ensureInitial = () => {
      if (
        currentValue.value == null &&
        props.autoSelectFirst &&
        normalizedOptions.value.length
      ) {
        const firstEnabled = normalizedOptions.value.find(
          (option) => !isOptionDisabled(option),
        );
        if (!firstEnabled) return;
        currentValue.value = firstEnabled.id;
        emit("update:modelValue", currentValue.value);
        emit("change", {
          id: currentValue.value,
          option: firstEnabled,
        });
      }
    };

    onMounted(() => {
      ensureInitial();
      if (props.automaticFocus) {
        requestAnimationFrame(() => focusActive());
      }
    });

    watch(
      () => props.modelValue,
      (val) => {
        currentValue.value = val;
      }
    );

    const isOptionDisabled = (opt) => !!opt[props.optionDisabledKey];

    const onSelect = (opt) => {
      if (props.disabled || isOptionDisabled(opt)) return;
      if (opt.id === currentValue.value) return; // idempotent
      currentValue.value = opt.id;
      emit("update:modelValue", currentValue.value);
      emit("change", { id: currentValue.value, option: opt });
    };

    const buttonsRef = ref([]);

    const registerBtn = (el, idx) => {
      if (el) buttonsRef.value[idx] = el;
    };

    const focusActive = () => {
      let idx = normalizedOptions.value.findIndex(
        (o) => o.id === currentValue.value && !isOptionDisabled(o),
      );
      if (idx === -1) {
        idx = normalizedOptions.value.findIndex(
          (option) => !isOptionDisabled(option),
        );
      }
      if (idx > -1 && buttonsRef.value[idx]) buttonsRef.value[idx].focus();
    };

    const moveFocus = (dir) => {
      const len = normalizedOptions.value.length;
      if (!len) return;
      let idx = buttonsRef.value.indexOf(document.activeElement);
      if (idx === -1) {
        idx = normalizedOptions.value.findIndex(
          (o) => o.id === currentValue.value
        );
      }
      if (idx === -1) idx = 0;

      let nextIdx = idx;
      for (let attempts = 0; attempts < len; attempts += 1) {
        nextIdx = (nextIdx + dir + len) % len;
        if (!isOptionDisabled(normalizedOptions.value[nextIdx])) break;
      }

      const opt = normalizedOptions.value[nextIdx];
      if (isOptionDisabled(opt)) return;
      if (props.pressOnFocus) {
        onSelect(opt);
      }
      buttonsRef.value[nextIdx]?.focus();
    };

    const onKeydown = (e, idx) => {
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          moveFocus(1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          moveFocus(-1);
          break;
        case "Home":
          e.preventDefault();
          if (normalizedOptions.value.length) {
            const firstIdx = normalizedOptions.value.findIndex(
              (option) => !isOptionDisabled(option),
            );
            if (firstIdx === -1) break;
            const opt = normalizedOptions.value[firstIdx];
            if (props.pressOnFocus) onSelect(opt);
            buttonsRef.value[firstIdx]?.focus();
          }
          break;
        case "End":
          e.preventDefault();
          if (normalizedOptions.value.length) {
            let lastIdx = normalizedOptions.value.length - 1;
            while (
              lastIdx >= 0 &&
              isOptionDisabled(normalizedOptions.value[lastIdx])
            ) {
              lastIdx -= 1;
            }
            if (lastIdx === -1) break;
            const opt = normalizedOptions.value[lastIdx];
            if (props.pressOnFocus) onSelect(opt);
            buttonsRef.value[lastIdx]?.focus();
          }
          break;
        case "Enter":
        case " ": // space
          e.preventDefault();
          const opt = normalizedOptions.value[idx];
          if (!isOptionDisabled(opt)) onSelect(opt);
          break;
      }
    };

    const getTabIndex = (opt, idx) => {
      if (props.disabled || isOptionDisabled(opt)) return -1;
      const selectedOption = normalizedOptions.value.find(
        (option) =>
          option.id === currentValue.value && !isOptionDisabled(option),
      );
      if (selectedOption) return opt.id === selectedOption.id ? 0 : -1;
      const firstEnabledIndex = normalizedOptions.value.findIndex(
        (option) => !isOptionDisabled(option),
      );
      if (idx === firstEnabledIndex) return 0;
      return -1;
    };

    const onFocus = (opt) => emit("focus", { id: opt.id, option: opt });
    const onBlur = (opt) => emit("blur", { id: opt.id, option: opt });

    // helpers for icons
    const isComponent = (icon) =>
      typeof icon === "object" || typeof icon === "function";
    const isSpriteRef = (icon) =>
      typeof icon === "string" && icon.includes("#");

    return {
      currentValue,
      normalizedOptions,
      onSelect,
      onKeydown,
      getTabIndex,
      isOptionDisabled,
      registerBtn,
      isComponent,
      isSpriteRef,
      onFocus,
      onBlur,
    };
  },
};
</script>

<style scoped>
.single-select-toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.single-select-toggle-row.no-wrap {
  flex-wrap: nowrap;
}

.single-select-toggle-row.full-width .single-toggle {
  flex: 1 1 0;
}
.single-toggle {
  position: relative;
  min-width: var(--control-height-compact);
  height: var(--control-height-micro);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--space-3);
  font-size: var(--font-size-micro);
  cursor: pointer;
  transition: background-color 0.15s, box-shadow 0.15s, color 0.15s;
  border-radius: var(--radius-sm);
  user-select: none;
  background: transparent;
  box-shadow: var(--shadow-surface-raised);
  color: var(--color-text-primary);
  border: 0;
}

.size-sm .single-toggle {
  height: var(--control-height-micro);
  font-size: var(--font-size-micro);
}

.size-md .single-toggle {
  height: var(--control-height-compact);
  font-size: var(--font-size-caption);
}

.size-lg .single-toggle {
  height: var(--control-height-default);
  padding-inline: var(--space-4);
  font-size: var(--font-size-label);
}

/* Disabled style consistency */
.single-select-toggle-row .single-toggle.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.single-toggle .abbr {
  letter-spacing: 0.7px;
  transform: translateY(1px);
}

.single-toggle.active {
  box-shadow: var(--shadow-surface-recessed-compact);
}
.single-toggle:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}
.single-toggle.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
/* Variants */
.variant-outline .single-toggle {
  background: transparent;
  border: 1px solid var(--color-border-control-strong);
  box-shadow: none;
}
.variant-outline .single-toggle.active {
  background: var(--color-surface-muted);
  box-shadow: var(--shadow-surface-recessed-compact);
}
.variant-ghost .single-toggle {
  background: transparent;
  box-shadow: none;
}
.variant-ghost .single-toggle.active {
  background: var(--color-surface-selected);
  box-shadow: none;
}

/* Segmented selection preserves the former toggle-switch pill dimensions. */
.single-select-toggle-row.variant-segmented {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  height: 31px;
  padding: 0 5px;
  gap: 2px;
  border-radius: 32px;
  background: var(--color-surface-raised);
  box-shadow: var(--shadow-segmented-track);
}

.variant-segmented .single-toggle {
  appearance: none;
  min-width: 21px;
  height: 21px;
  padding: 0 var(--space-4);
  border-radius: 24px;
  background-color: transparent;
  box-shadow: none;
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--font-size-label);
  white-space: nowrap;
  transition:
    background-color var(--motion-standard) cubic-bezier(0.85, 0.05, 0.18, 1.35),
    color var(--motion-standard) cubic-bezier(0.85, 0.05, 0.18, 1.35);
}

.variant-segmented .single-toggle:focus-visible {
  outline: 2px solid var(--color-accent-emphasis);
  outline-offset: 2px;
}

.variant-segmented .single-toggle:hover:not(.active) {
  color: var(--color-text-secondary);
}

.variant-segmented .single-toggle.active {
  background-color: var(--color-surface-raised);
  box-shadow: none;
  color: var(--color-text-primary);
}

.variant-segmented .label {
  letter-spacing: normal;
}

.single-select-toggle-row.variant-segmented.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.variant-segmented.disabled .single-toggle {
  cursor: not-allowed;
  pointer-events: none;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  margin-right: 4px;
}
.label,
.abbr {
  letter-spacing: 0.5px;
}

@media (prefers-reduced-motion: reduce) {
  .single-toggle {
    transition: none;
  }
}
</style>
